import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { Template } from "@/types/template";
import { useCallback, useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ExternalLink } from "@/components/OptionalLink";
import { DigitalOceanReferral } from "@/components/DigitalOceanReferral";
import { Stepper } from "@/components/Stepper";
import { LaunchHeader } from "@/components/launch/LaunchHeader";
import { ProviderType, ProviderTypes } from "@/types";
import { getProviderMeta } from "@/lib/provider/meta";
import { ProviderConfigMatch } from "@/lib/provider/config";
import { useLocalStorage } from "@/components/useLocalStorage";
import { DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { DropdownMenu } from "@/components/ui/dropdown-menu";
import { DropdownMenuContent } from "@/components/ui/dropdown-menu";
import { ChevronDown, MoreVertical } from "lucide-react";
import { cn, fetcherTyped, isProd, nonFalse } from "@/lib/utils";
import { SSHKeyListResponseSchema } from "@/types/schemas";

export const ProvisionHost = ({
  template,
  provider,
  onNext,
  setProvider
}: {
  template: Template;
  provider: ProviderType;
  setProvider?: (provider: ProviderType) => void;
  onNext?: (
    choice: "host" | "provision" | "existing" | "launch",
    provision?: {
      provider: ProviderType;
      providerType: string;
      regionId: string;
      sshKeyId?: string;
    }
  ) => void;
}) => {
  const { data: providers } = useSWR<ProviderType[]>("/api/tenant/provider");
  const connected = useMemo(() => providers?.includes(provider), [providers, provider]);
  const meta = getProviderMeta(provider);
  const [regionId, setRegionId] = useState<string | undefined>();
  const [instanceType, setInstanceType] = useState<string | undefined>();
  const { data: sshKeys } = useSWR(
    "/api/settings/privateKey",
    fetcherTyped(SSHKeyListResponseSchema)
  );
  const [sshKeyId, setSshKeyId] = useState<string | undefined>(undefined);
  const [billing, setBilling] = useLocalStorage<boolean>(provider + ".billing.setup", false);

  const { data: config, isValidating } = useSWR<ProviderConfigMatch>(
    connected
      ? `/api/provider/${provider}?${new URLSearchParams({
          memory: template.requirements?.memory?.toString() ?? "1024",
          cpu: template.requirements?.cpu?.toString() ?? "1",
          disk: template.requirements?.storage?.toString() ?? "10",
          arch: "amd64",
          ...(regionId ? { region: regionId } : {})
        })}`
      : null
  );

  const handleChangeProvider = useCallback(
    (provider: ProviderType) => {
      if (setProvider) {
        setRegionId(undefined);
        setInstanceType(undefined);
        setProvider(provider);
      }
    },
    [setProvider]
  );

  useEffect(() => {
    if (config) {
      setRegionId(regionId =>
        regionId
          ? (config.regions.find(r => r.id === regionId)?.id ?? config.defaultRegion)
          : config.defaultRegion
      );
      setInstanceType(instanceType =>
        instanceType
          ? config.types[instanceType]
            ? instanceType
            : config.typeName
          : config.typeName
      );
    }
  }, [config]);

  useEffect(() => {
    if (sshKeys && sshKeys.length > 0 && sshKeyId === undefined) {
      setSshKeyId(sshKeys[0].id);
    }
  }, [sshKeys, sshKeyId]);

  return (
    <div className="flex flex-col justify-center items-center w-full min-h-screen p-6">
      <LaunchHeader
        name={template?.name}
        logo={template?.logo}
        subtitle="Automatically create a new VPS"
        className="mb-2"
      />

      <div className="mt-6 flex flex-col md:flex-row gap-6 mb-8">
        <Card className="w-[300px] md:w-[500px]">
          <CardHeader>
            <CardTitle className="text-xl flex justify-between items-center">
              <span>Create a new server</span>

              <div className="flex flex-row gap-1">
                <div className="flex items-center">
                  <Button
                    variant="secondary"
                    className="rounded-r-none h-8"
                    size="sm"
                    onClick={() => setProvider && setProvider(provider)}
                    disabled={!setProvider}
                  >
                    {meta?.name}
                  </Button>
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild disabled={!setProvider}>
                      <Button
                        variant="secondary"
                        className="rounded-l-none border-l border-secondary px-2 h-8"
                        size="sm"
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      {ProviderTypes.map(getProviderMeta)
                        .filter(nonFalse)
                        .filter(p => p.compute)
                        .filter(p => (p.experimental ? !isProd() : true))
                        .map(p => (
                          <DropdownMenuItem
                            key={p.id}
                            onClick={() => handleChangeProvider(p.id)}
                            className={cn(
                              p.id === provider
                                ? "bg-primary hover:bg-primary/90 text-primary-foreground"
                                : "hover:bg-secondary/50",
                              "flex gap-1"
                            )}
                          >
                            {p.name}
                            {p.experimental ? (
                              <span className="text-xs text-muted-foreground">
                                (experimental)
                              </span>
                            ) : null}
                          </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      aria-haspopup="true"
                      size="icon"
                      variant="secondary"
                      className="h-8"
                      animations="none"
                    >
                      <MoreVertical className="h-4 w-4" />
                      <span className="sr-only">Toggle menu</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem asChild>
                      <Link href="/settings/integrations">Manage in settings</Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardTitle>
            <CardDescription>Set up a new server</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4 max-w-[500px]">
            {connected ? (
              <div>
                {meta?.confirmBilling ? (
                  <div className="flex flex-row gap-2 items-center mb-4">
                    <Checkbox
                      id="billing"
                      checked={billing}
                      onCheckedChange={checked => setBilling(checked as boolean)}
                    />
                    <Label htmlFor="billing" className="text-sm inline">
                      I&apos;ve setup{" "}
                      <ExternalLink
                        className="link"
                        href="https://cloud.digitalocean.com/account/billing"
                      >
                        billing
                      </ExternalLink>{" "}
                      for my {meta?.name} account (required for the API to work)
                    </Label>
                  </div>
                ) : null}

                <div>
                  <span className="text-secondary-foreground text-sm">
                    Fine-tune your new VPS (optional)
                  </span>
                </div>

                <div className="mt-4 flex flex-col md:flex-row gap-2 flex-wrap">
                  <Select
                    value={regionId}
                    onValueChange={setRegionId}
                    disabled={(meta?.confirmBilling && !billing) || isValidating}
                  >
                    <SelectTrigger className="text-left">
                      <SelectValue placeholder="Closest region" />
                    </SelectTrigger>
                    <SelectContent>
                      {config?.regions.map(r => (
                        <SelectItem key={r.id} value={r.id}>
                          {r.label} ({r.id})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select
                    value={instanceType}
                    onValueChange={setInstanceType}
                    disabled={(meta?.confirmBilling && !billing) || isValidating}
                  >
                    <SelectTrigger className="text-left">
                      <SelectValue placeholder="Fine-tune VPS" className="border text-left" />
                    </SelectTrigger>
                    <SelectContent>
                      {config &&
                        Object.entries(config.available).map(([t, type]) => (
                          <SelectItem
                            key={t}
                            value={t}
                            className={config.types[t] ? "" : "text-muted-foreground"}
                          >
                            {t} ({Math.floor(type.memory / 1024)}GB RAM, {type.cpu} vCPU
                            {type.disk ? `, ${type.disk}GB disk` : ""})
                            {type.note ? ` - ${type.note}` : ""}
                            {config.types[t] ? "" : " - not recommended"}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>

                  <Select
                    value={sshKeyId ?? "auto"}
                    onValueChange={setSshKeyId}
                    disabled={meta?.confirmBilling && !billing}
                  >
                    <SelectTrigger className="text-left">
                      <SelectValue placeholder="Create SSH key" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Create SSH key</SelectItem>
                      {sshKeys?.map(k => (
                        <SelectItem key={k.id} value={k.id}>
                          {k.name} (Existing SSH key)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : null}

            {connected || provider !== "do" ? null : <DigitalOceanReferral />}

            <div className="flex flex-col md:flex-row gap-2 text-sm items-center justify-end">
              {connected ? (
                <span className="text-secondary-foreground text-sm">
                  <b>Attention!</b> {meta?.name}{" "}
                  {meta?.description ? `(${meta?.description})` : ""} will charge you for this
                  server, if you don&apos;t cancel or credits run out.
                </span>
              ) : (
                <span>
                  Give us access to your {meta?.name} account so we can create a new VPS for
                  you.
                </span>
              )}

              {!connected ? (
                <Button asChild className="h-10 w-50" variant="default">
                  <Link
                    href={
                      meta?.authType === "oauth"
                        ? `/api/provider/${provider}/connect?callbackUrl=/r/${template.id}/provision`
                        : "/settings/integrations"
                    }
                  >
                    {meta?.authType === "oauth"
                      ? `Authorize on ${meta?.name}`
                      : "Configure in Settings"}
                  </Link>
                </Button>
              ) : null}
            </div>
          </CardContent>
        </Card>
      </div>

      <Button
        disabled={
          !instanceType || !regionId || !connected || (meta?.confirmBilling && !billing)
        }
        size="lg"
        onClick={() =>
          onNext &&
          instanceType &&
          regionId &&
          onNext("launch", {
            providerType: instanceType,
            regionId,
            provider,
            sshKeyId: sshKeyId === "auto" ? undefined : sshKeyId
          })
        }
      >
        Next
      </Button>

      <div className="mt-8">
        <Stepper
          steps={3}
          current={1}
          onStep={step => {
            if (step === 0) {
              onNext && onNext("host");
            }
          }}
        />
      </div>
    </div>
  );
};
